import React from 'react';
import styled from 'styled-components';
import { useFormik, FormikProvider } from 'formik';
import { object, string, array } from 'yup';
import { Stack, Typography, Link, Box } from '@mui/material';
import { PublicationAssessmentsContent } from './PublicationAssessmentsContent';
import type {
  PublicationAssessmentsType,
  PublicationAssessmentsValue,
} from './types';
import { PAGE_TEXT } from '../../../../lang';
import { Button } from '../../../../../../components';

type PublicationAssessmentsBoxProps = {
  initialValues: PublicationAssessmentsType[];
  isViewMode: boolean;
  onJumpToNext?: () => void;
  onSubmit: (values: PublicationAssessmentsValue) => void;
};

const presentHKPFS = process.env.REACT_APP_HKPFS_ON === 'true';

export const PublicationAssessmentsBox: React.FunctionComponent<
  PublicationAssessmentsBoxProps
> = props => {
  const { DRC_ASSESSMENT_DETAILS } = PAGE_TEXT;
  const STEP_TEXT = DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_B;
  const { initialValues, isViewMode, onSubmit, onJumpToNext } = props;

  const formik = useFormik<PublicationAssessmentsValue>({
    initialValues: {
      publicationAssessments: initialValues,
    },
    validationSchema: object({
      publicationAssessments: array().of(
        object({
          publicationReferenceId: string().required('Required'),
          publicationType: string().required('Required'),
        }),
      ),
    }),
    onSubmit,
  });

  return (
    <ContentBox>
      <Stack>
        {!isViewMode && (
          <Box padding={2}>
            <Typography variant="subtitle2" color="#951D1D">
              {STEP_TEXT.SPECIAL_LIST.F}
              <Link
                href={STEP_TEXT.SPECIAL_LIST.URL}
                target="_blank"
                rel="noopener"
                underline="always"
              >
                <b>{STEP_TEXT.SPECIAL_LIST.LABEL}</b>
              </Link>
              {STEP_TEXT.SPECIAL_LIST.L}
            </Typography>

            {presentHKPFS && (
              <Typography variant="subtitle2">
                {STEP_TEXT.NOTE.TITLE}
                <Link
                  href={STEP_TEXT.NOTE.URL}
                  target="_blank"
                  rel="noopener"
                  underline="always"
                  color="#000"
                >
                  <b>{STEP_TEXT.NOTE.LABEL}</b>
                </Link>
                {STEP_TEXT.SPECIAL_LIST.L}
              </Typography>
            )}
          </Box>
        )}

        {initialValues.length > 0 ? (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} noValidate>
              {initialValues.map((_, index: number) => (
                <PublicationAssessmentsContent
                  key={index}
                  index={index}
                  isViewMode={isViewMode}
                />
              ))}

              <div className="save-area">
                {!isViewMode && (
                  <>
                    <Button
                      variant="contained"
                      className="!mr-4"
                      color="primary"
                      type="submit"
                    >
                      {PAGE_TEXT.SAVE}
                    </Button>
                    <Button onClick={onJumpToNext}>{PAGE_TEXT.NEXT}</Button>
                  </>
                )}
              </div>
            </form>
          </FormikProvider>
        ) : (
          <div className="empty-text">
            <h2>{DRC_ASSESSMENT_DETAILS.EMPTY_TEXT.PUBLICATION}&nbsp;</h2>
            {!isViewMode && (
              <p
                className="cursor-pointer underline decoration-1 text-[#951d1d]"
                onClick={onJumpToNext}
              >
                {PAGE_TEXT.MOVE_NEXT}
              </p>
            )}
          </div>
        )}
      </Stack>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  form {
    padding: 1rem;
  }

  .empty-text {
    display: flex;
    justify-content: center;
    padding: 1rem;
    text-wrap: wrap;
  }

  .save-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
`;
